import { useSearchParams } from 'react-router-dom';
import { Select } from '../components/ui/Select';
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  ChevronDown,
  ChevronRight,
  Plus,
  Trash2,
  Save,
  Package,
  Truck,
  CreditCard,
  Building2,
  Loader2,
  Search,
  AlertCircle,
  CheckCircle,
  Info,
  Megaphone,
  X,
  Link2 as LinkIcon,
  Clock,
  Scale,
  Sigma,
} from 'lucide-react';
import { financeApi, storeApi } from '../lib/api';
import { EmptyState } from '../components/EmptyState';
import { useConfirmDialog } from '../contexts/ConfirmDialogContext';
import { FinanceCostSnapshotPanel } from '../components/FinanceCostSnapshotPanel';
import { FinanceMetaCostPanel } from '../components/finance/FinanceMetaCostPanel';
import { FinancePeriodCostPanel } from '../components/finance/FinancePeriodCostPanel';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ProductCost {
  id?: string;
  externalProductId: string;
  productName: string;
  sku?: string;
  sellingPrice: number;
  purchasePrice: number;
  storeId?: string | null;
  hasWarehouseProduct?: boolean;
  historyCount?: number;
}

interface ProductCostsResponse {
  products: ProductCost[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

interface ShippingWeightTier {
  weightFromKg: number;
  weightToKg: number | null;
  costNet: number;
}

interface ShippingMethod {
  storeId?: string | null;
  id?: string;
  shippingMethodKey: string;
  shippingMethodName: string;
  costNet: number;
  returnCostNet?: number | null;
  weightTiers?: ShippingWeightTier[];
  isEnabled: boolean;
  historyCount?: number;
}

interface TransactionFee {
  storeId?: string | null;
  id: string;
  name: string;
  paymentMethod: string | null;
  percentFee: number;
  fixedFee: number;
  chargebackFee?: number;
  isActive: boolean;
  historyCount?: number;
}

interface FixedCost {
  storeId?: string | null;
  id: string;
  name: string;
  category: string;
  amountNet: number;
  isActive: boolean;
  historyCount?: number;
}

type FixedCostCategory = 'office' | 'hosting' | 'accounting' | 'subscriptions' | 'other';

const FIXED_COST_CATEGORIES: { value: FixedCostCategory; label: string }[] = [
  { value: 'office', label: 'Biuro' },
  { value: 'hosting', label: 'Hosting / Serwery' },
  { value: 'accounting', label: 'Księgowość' },
  { value: 'subscriptions', label: 'Subskrypcje' },
  { value: 'other', label: 'Inne' },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatPLN(value: number): string {
  return new Intl.NumberFormat('pl-PL', {
    style: 'currency',
    currency: 'PLN',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

// ---------------------------------------------------------------------------
// Collapsible Section wrapper
// ---------------------------------------------------------------------------

function Section({
  title,
  icon,
  expanded,
  onToggle,
  children,
}: {
  title: string;
  icon: React.ReactNode;
  expanded: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="bg-surface-raised/50 border border-border-subtle rounded-2xl overflow-hidden">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center gap-3 px-6 py-4 text-left hover:bg-surface-hover/30 transition-colors"
      >
        {expanded ? (
          <ChevronDown className="w-5 h-5 text-accent-primary flex-shrink-0" />
        ) : (
          <ChevronRight className="w-5 h-5 text-text-secondary flex-shrink-0" />
        )}
        <span className="text-accent-primary flex-shrink-0">{icon}</span>
        <h2 className="text-lg font-semibold text-text-primary">{title}</h2>
      </button>
      {expanded && (
        <div className="px-6 pb-6 pt-2 border-t border-border-subtle">
          {children}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Toast-style feedback
// ---------------------------------------------------------------------------

function Feedback({ type, message }: { type: 'success' | 'error'; message: string }) {
  if (!message) return null;
  const isError = type === 'error';
  return (
    <div
      className={`flex items-center gap-2 p-3 rounded-xl text-sm mb-4 ${
        isError
          ? 'bg-danger/10 border border-danger/30 text-danger'
          : 'bg-success/10 border border-success/30 text-success'
      }`}
    >
      {isError ? <AlertCircle className="w-4 h-4 flex-shrink-0" /> : <CheckCircle className="w-4 h-4 flex-shrink-0" />}
      {message}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Price History Panel (date-range pricing)
// ---------------------------------------------------------------------------

interface CostHistoryEntry {
  id: string;
  validFrom: string;
  validTo: string | null;
  // product
  purchasePrice?: number;
  // shipping
  costNet?: number;
  // fee
  percentFee?: number;
  fixedFee?: number;
  // fixed
  amountNet?: number;
  createdAt?: string;
}

function PriceHistoryPanel({
  parentId,
  costType,
  storeId,
  onPriceUpdated,
}: {
  parentId: string;
  costType: 'product' | 'shipping' | 'fee' | 'fixed';
  storeId: string | null;
  onPriceUpdated?: () => void;
}) {
  const confirmDialog = useConfirmDialog();
  const [entries, setEntries] = useState<CostHistoryEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newValidFrom, setNewValidFrom] = useState('');
  const [newValidTo, setNewValidTo] = useState('');
  const [indefinite, setIndefinite] = useState(true);
  const [newValue, setNewValue] = useState('');
  const [newPercentFee, setNewPercentFee] = useState('');
  const [newFixedFee, setNewFixedFee] = useState('');
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const loadHistory = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      let res;
      switch (costType) {
        case 'product':
          res = await financeApi.getProductCostHistory(parentId);
          break;
        case 'shipping':
          res = await financeApi.getShippingCostHistory(parentId);
          break;
        case 'fee':
          res = await financeApi.getTransactionFeeHistory(parentId);
          break;
        case 'fixed':
          res = await financeApi.getFixedCostHistory(parentId);
          break;
      }
      const data = (res.data ?? res) as { history: CostHistoryEntry[] };
      setEntries(data.history ?? []);
    } catch {
      setError('Nie udało się wczytać historii cen');
    } finally {
      setLoading(false);
    }
  }, [parentId, costType]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  const formatDate = (dateStr: string) => {
    try {
      const date = new Date(dateStr);
      if (costType === 'product' && date.getTime() === 0) return 'Od początku';
      return costType === 'product'
        ? date.toLocaleString('pl-PL', { timeZone: 'Europe/Warsaw', dateStyle: 'short', timeStyle: 'medium' })
        : date.toLocaleDateString('pl-PL');
    } catch {
      return dateStr;
    }
  };

  const getValueLabel = (entry: CostHistoryEntry): string => {
    switch (costType) {
      case 'product':
        return formatPLN(entry.purchasePrice ?? 0);
      case 'shipping':
        return formatPLN(entry.costNet ?? 0);
      case 'fee':
        return `${entry.percentFee ?? 0}% + ${formatPLN(entry.fixedFee ?? 0)}`;
      case 'fixed':
        return formatPLN(entry.amountNet ?? 0);
      default:
        return '-';
    }
  };

  const getValueHeader = (): string => {
    switch (costType) {
      case 'product': return 'Koszt zakupu';
      case 'shipping': return 'Koszt netto';
      case 'fee': return 'Prowizja';
      case 'fixed': return 'Kwota netto';
      default: return 'Wartość';
    }
  };

  const handleAdd = async () => {
    if (!newValidFrom) return;
    if (!indefinite && !newValidTo) return;
    setSaving(true);
    setError(null);
    try {
      const validToValue = indefinite ? null : newValidTo;
      switch (costType) {
        case 'product':
          await financeApi.addProductCostHistory(parentId, {
            purchasePrice: parseFloat(newValue) || 0,
            validFrom: newValidFrom,
            validTo: validToValue,
          });
          break;
        case 'shipping':
          await financeApi.addShippingCostHistory(parentId, {
            costNet: parseFloat(newValue) || 0,
            validFrom: newValidFrom,
            validTo: validToValue,
          });
          break;
        case 'fee':
          await financeApi.addTransactionFeeHistory(parentId, {
            percentFee: parseFloat(newPercentFee) || 0,
            fixedFee: parseFloat(newFixedFee) || 0,
            validFrom: newValidFrom,
            validTo: validToValue,
          });
          break;
        case 'fixed':
          await financeApi.addFixedCostHistory(parentId, {
            amountNet: parseFloat(newValue) || 0,
            validFrom: newValidFrom,
            validTo: validToValue,
          });
          break;
      }
      setShowAddForm(false);
      setNewValidFrom('');
      setNewValidTo('');
      setIndefinite(true);
      setNewValue('');
      setNewPercentFee('');
      setNewFixedFee('');
      await loadHistory();
      onPriceUpdated?.();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error
        || 'Nie udało się dodać wpisu historii';
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (historyId: string) => {
    if (!(await confirmDialog({
      title: 'Usuń wpis historii',
      message: 'Usunąć ten wpis historii cen?',
      confirmLabel: 'Usuń',
    }))) return;
    setDeleting(historyId);
    setError(null);
    try {
      switch (costType) {
        case 'product':
          await financeApi.deleteProductCostHistory(historyId);
          break;
        case 'shipping':
          await financeApi.deleteShippingCostHistory(historyId);
          break;
        case 'fee':
          await financeApi.deleteTransactionFeeHistory(historyId);
          break;
        case 'fixed':
          await financeApi.deleteFixedCostHistory(historyId);
          break;
      }
      await loadHistory();
      onPriceUpdated?.();
    } catch {
      setError('Nie udało się usunąć wpisu');
    } finally {
      setDeleting(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-4 bg-surface-hover/20 border-t border-border-subtle">
        <Loader2 className="w-4 h-4 animate-spin text-accent-primary" />
        <span className="ml-2 text-xs text-text-muted">Ładowanie historii...</span>
      </div>
    );
  }

  return (
    <div className="bg-surface-hover/20 border-t border-border-subtle px-4 py-3 space-y-3">
      <FinanceCostSnapshotPanel ruleId={parentId} storeId={storeId}
        kind={({product:'PRODUCT_COST',shipping:'SHIPPING_COST',fee:'TRANSACTION_FEE',fixed:'FIXED_COST'} as const)[costType]} />
      <div className="flex items-center justify-between">
        <h4 className="text-xs font-medium text-text-secondary flex items-center gap-1.5">
          <Clock className="w-3.5 h-3.5" />
          Historia cen
        </h4>
        {!showAddForm && (
          <button
            onClick={() => setShowAddForm(true)}
            className="text-xs text-accent-primary hover:text-accent-primary/80 flex items-center gap-1 transition-colors"
          >
            <Plus className="w-3 h-3" />
            Dodaj nową cenę
          </button>
        )}
      </div>

      {error && (
        <div className="flex items-center gap-1.5 text-xs text-danger">
          <AlertCircle className="w-3 h-3" />
          {error}
        </div>
      )}

      {/* Add form */}
      {showAddForm && (
        <div className="p-3 bg-surface-raised/50 rounded-lg border border-border-subtle space-y-3">
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex flex-col gap-1">
              <label className="text-[11px] text-text-muted">Obowiązuje od</label>
              <input
                type="date"
                value={newValidFrom}
                onChange={(e) => setNewValidFrom(e.target.value)}
                className="input text-sm w-40"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-[11px] text-text-muted">Obowiązuje do</label>
              <input
                type="date"
                value={newValidTo}
                onChange={(e) => setNewValidTo(e.target.value)}
                disabled={indefinite}
                min={newValidFrom || undefined}
                className="input text-sm w-40 disabled:opacity-30 disabled:cursor-not-allowed"
              />
            </div>
            <label className="flex items-center gap-1.5 cursor-pointer pb-2">
              <input
                type="checkbox"
                checked={indefinite}
                onChange={(e) => { setIndefinite(e.target.checked); if (e.target.checked) setNewValidTo(''); }}
                className="accent-accent-primary w-3.5 h-3.5"
              />
              <span className="text-[11px] text-text-secondary">Bezterminowo</span>
            </label>
            {costType === 'fee' ? (
              <>
                <div className="flex flex-col gap-1">
                  <label className="text-[11px] text-text-muted">Prowizja %</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={newPercentFee}
                    onChange={(e) => setNewPercentFee(e.target.value)}
                    className="input text-right text-sm w-28"
                    placeholder="0.00"
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <label className="text-[11px] text-text-muted">Stała opłata (PLN)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={newFixedFee}
                    onChange={(e) => setNewFixedFee(e.target.value)}
                    className="input text-right text-sm w-28"
                    placeholder="0.00"
                  />
                </div>
              </>
            ) : (
              <div className="flex flex-col gap-1">
                <label className="text-[11px] text-text-muted">{getValueHeader()} (PLN)</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={newValue}
                  onChange={(e) => setNewValue(e.target.value)}
                  className="input text-right text-sm w-32"
                  placeholder="0.00"
                />
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleAdd}
              disabled={saving || !newValidFrom || (!indefinite && !newValidTo)}
              className="px-3 py-1.5 rounded-lg text-xs font-medium bg-accent-primary/10 text-accent-primary hover:bg-accent-primary/20 transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1"
            >
              {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
              Zapisz
            </button>
            <button
              onClick={() => {
                setShowAddForm(false);
                setNewValidFrom('');
                setNewValidTo('');
                setIndefinite(true);
                setNewValue('');
                setNewPercentFee('');
                setNewFixedFee('');
                setError(null);
              }}
              className="px-3 py-1.5 rounded-lg text-xs font-medium text-text-secondary hover:text-text-primary hover:bg-surface-hover/50 transition-colors"
            >
              Anuluj
            </button>
          </div>
        </div>
      )}

      {/* History table */}
      {entries.length === 0 ? (
        <p className="text-xs text-text-muted py-2">Brak historii cen — obowiązuje tylko aktualna cena.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-border-subtle text-text-muted text-left">
                <th className="pb-2 pr-3 font-medium">Od daty</th>
                <th className="pb-2 pr-3 font-medium">Do daty</th>
                <th className="pb-2 pr-3 font-medium text-right">{getValueHeader()}</th>
                <th className="pb-2 font-medium text-right w-16">Akcje</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((entry) => (
                <tr key={entry.id} className="border-b border-border-subtle last:border-0 hover:bg-surface-hover/30">
                  <td className="py-2 pr-3 text-text-secondary">{formatDate(entry.validFrom)}</td>
                  <td className="py-2 pr-3">
                    {entry.validTo === null ? (
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-success/10 text-success border border-success/20">
                        Teraz
                      </span>
                    ) : (
                      <span className="text-text-secondary">{formatDate(entry.validTo)}</span>
                    )}
                  </td>
                  <td className="py-2 pr-3 text-right text-text-primary font-medium">{getValueLabel(entry)}</td>
                  <td className="py-2 text-right">
                    <button
                      onClick={() => handleDelete(entry.id)}
                      disabled={deleting === entry.id}
                      className="p-1 text-text-muted hover:text-danger hover:bg-danger/10 rounded transition-colors disabled:opacity-40"
                      title="Usuń wpis"
                    >
                      {deleting === entry.id ? (
                        <Loader2 className="w-3 h-3 animate-spin" />
                      ) : (
                        <Trash2 className="w-3 h-3" />
                      )}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Section 1 -- Koszty zakupu produktów
// ---------------------------------------------------------------------------

function ProductCostsSection({ isVat, vatRate, fixedStoreId, initialSearch }: { isVat: boolean; vatRate: number; fixedStoreId?: string; initialSearch?: string }) {
  const [products, setProducts] = useState<ProductCost[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState(initialSearch ?? '');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [expandedHistoryId, setExpandedHistoryId] = useState<string | null>(null);
  const limit = 20;

  // Store filter (only used when not fixedStoreId)
  const [stores, setStores] = useState<{ id: string; name: string }[]>([]);
  const [selectedStoreId, setSelectedStoreId] = useState<string>(fixedStoreId || 'all');

  useEffect(() => {
    if (!fixedStoreId || fixedStoreId === 'unassigned') {
      storeApi.getAll().then((res) => {
        const list = ((res.data ?? res) as { id: string; name: string }[]) || [];
        setStores(list);
      }).catch(() => {});
    }
  }, [fixedStoreId]);

  const effectiveStoreId = fixedStoreId || selectedStoreId;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, unknown> = { page, limit, search: search || undefined };
      if (effectiveStoreId && effectiveStoreId !== 'all') {
        params.storeId = effectiveStoreId;
      }
      const { data } = await financeApi.getProductCosts(params as Parameters<typeof financeApi.getProductCosts>[0]);
      const res = data as unknown as ProductCostsResponse;
      setProducts(res.products ?? []);
      setTotalPages(res.pagination?.totalPages ?? 1);
      setTotal(res.pagination?.total ?? 0);
    } catch {
      setFeedback({ type: 'error', message: 'Nie udało się wczytać produktów' });
    } finally {
      setLoading(false);
    }
  }, [page, search, effectiveStoreId]);

  useEffect(() => {
    load();
  }, [load]);

  // Reset to page 1 when search changes
  useEffect(() => {
    setPage(1);
  }, [search]);

  const handlePriceChange = (idx: number, value: string) => {
    setProducts((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], purchasePrice: parseFloat(value) || 0 };
      return next;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    setFeedback(null);
    try {
      await financeApi.bulkSaveProductCosts(
        products.map((p) => ({
          id: p.id,
          storeId: p.storeId,
          externalProductId: p.externalProductId,
          productName: p.productName,
          sellingPrice: p.sellingPrice,
          purchasePrice: p.purchasePrice,
          sku: p.sku,
        })),
      );
      setFeedback({ type: 'success', message: 'Koszty zakupu zostały zapisane' });
      if (fixedStoreId === 'unassigned') await load();
    } catch {
      setFeedback({ type: 'error', message: 'Nie udało się zapisać kosztów zakupu' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      {feedback && <Feedback type={feedback.type} message={feedback.message} />}

      <div className="flex items-center gap-2 p-3 bg-accent-primary/10 border border-accent-primary/30 rounded-xl text-sm text-text-secondary">
        <Info className="w-4 h-4 text-accent-primary flex-shrink-0" />
        {isVat ? 'Podawaj kwoty NETTO (bez VAT)' : 'Podawaj kwoty BRUTTO'}
      </div>

      {/* Store filter (badge-style) + Search */}
      <div className="flex flex-wrap items-center gap-x-2 gap-y-2">
        {!fixedStoreId && stores.length > 1 && (
          <div className="flex items-center gap-2">
            <span className="text-sm leading-none" title="Filtry sklepowe">🏪</span>
            <button
              onClick={() => { setSelectedStoreId('all'); setPage(1); }}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                selectedStoreId === 'all'
                  ? 'bg-purple-500/20 text-purple-300 border border-purple-500/30'
                  : 'bg-surface-hover/50 text-text-secondary border border-border-subtle hover:text-text-primary hover:border-border'
              }`}
            >
              Wszystkie sklepy
            </button>
            {stores.map((s) => (
              <button
                key={s.id}
                onClick={() => { setSelectedStoreId(s.id); setPage(1); }}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                  selectedStoreId === s.id
                    ? 'bg-purple-500/20 text-purple-300 border border-purple-500/30'
                    : 'bg-surface-hover/50 text-text-secondary border border-border-subtle hover:text-text-primary hover:border-border'
                }`}
              >
                {s.name}
              </button>
            ))}
          </div>
        )}
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Szukaj produktu po nazwie, SKU lub ID..."
            className="input pl-10"
          />
        </div>
      </div>

      {/* Warning about products missing from warehouse */}
      {!loading && products.some(p => p.hasWarehouseProduct === false) && (
        <div className="flex items-start gap-2 px-3 py-2.5 rounded-lg bg-amber-500/10 border border-amber-500/20 text-xs text-amber-300/90 mb-3">
          <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <div>
            <span className="font-medium">Niektóre produkty nie są dodane do magazynu.</span>{' '}
            Ich cena sprzedaży jest pobierana z zamówień i może być niedokładna (np. cena po rabacie, cena netto zamiast brutto). Dodaj te produkty do{' '}
            <a href="/warehouse" className="underline hover:text-amber-200">magazynu</a> i powiąż z WooCommerce, aby cena katalogowa była prawidłowa.
          </div>
        </div>
      )}

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-accent-primary" />
        </div>
      ) : products.length === 0 ? (
        <EmptyState
          icon={Package}
          title="Brak produktów do wyświetlenia"
          size="sm"
        />
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border-subtle text-text-secondary text-left">
                  <th className="pb-3 pr-4 font-medium">Produkt</th>
                  <th className="pb-3 pr-4 font-medium text-right">Cena sprzedaży (brutto)</th>
                  <th className="pb-3 pr-4 font-medium text-right">Koszt zakupu ({isVat ? 'netto' : 'brutto'})</th>
                  <th className="pb-3 pr-4 font-medium text-right">Marża{isVat ? ' (netto)' : ''}</th>
                  <th className="pb-3 font-medium text-center w-10"></th>
                </tr>
              </thead>
              <tbody>
                {products.map((p, idx) => {
                  const sellingNet = isVat ? p.sellingPrice / (1 + vatRate / 100) : p.sellingPrice;
                  const margin = sellingNet - p.purchasePrice;
                  const isHistoryExpanded = expandedHistoryId === p.id;
                  return (
                    <React.Fragment key={p.id}>
                      <tr className="border-b border-border-subtle hover:bg-surface-hover/30">
                        <td className="py-3 pr-4">
                          <div className="text-text-primary font-medium">{p.productName}</div>
                          <div className="text-xs text-text-muted">
                            ID: {p.externalProductId}{p.sku ? ` | SKU: ${p.sku}` : ''}
                            {!fixedStoreId && effectiveStoreId === 'all' && p.storeId && stores.length > 0 && (() => {
                              const store = stores.find(s => s.id === p.storeId);
                              return store ? <span className="ml-1 text-accent-primary/60">| {store.name}</span> : null;
                            })()}
                          </div>
                          {fixedStoreId === 'unassigned' && <Select aria-label={`Sklep dla ${p.productName}`} value={p.storeId || ''} className="mt-2" onChange={event => setProducts(previous => previous.map(product => product.id === p.id ? { ...product, storeId: event.target.value || null } : product))}>
                            <option value="">Wybierz sklep</option>
                            {stores.map(store => <option key={store.id} value={store.id}>{store.name}</option>)}
                          </Select>}
                        </td>
                        <td className="py-3 pr-4 text-right text-text-secondary whitespace-nowrap">
                          <span className="inline-flex items-center gap-1.5">
                            {p.hasWarehouseProduct === false && (
                              <span className="relative group cursor-help">
                                <AlertCircle className="w-3.5 h-3.5 text-amber-400/80" />
                                <span className="absolute bottom-full right-0 mb-1.5 w-56 px-2.5 py-1.5 rounded-lg bg-surface-hover border border-border text-xs text-text-secondary opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity z-10 text-left">
                                  Cena pobrana z zamówienia - może być niedokładna. Dodaj produkt do magazynu, aby powiązać cenę katalogową.
                                </span>
                              </span>
                            )}
                            {formatPLN(p.sellingPrice)}
                          </span>
                        </td>
                        <td className="py-3 pr-4 text-right">
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={p.purchasePrice || ''}
                            onChange={(e) => handlePriceChange(idx, e.target.value)}
                            className="input w-32 text-right text-sm ml-auto"
                            placeholder="0.00"
                          />
                        </td>
                        <td className={`py-3 pr-4 text-right whitespace-nowrap font-medium ${margin >= 0 ? 'text-success' : 'text-danger'}`}>
                          {formatPLN(margin)}
                        </td>
                        <td className="py-3 text-center">
                          {p.id && (
                            <button
                              onClick={() => setExpandedHistoryId(isHistoryExpanded ? null : p.id ?? null)}
                              className={`p-1.5 rounded-lg transition-colors ${
                                isHistoryExpanded
                                  ? 'text-accent-primary bg-accent-primary/10'
                                  : (p.historyCount ?? 0) > 0
                                    ? 'text-accent-primary hover:bg-accent-primary/10'
                                    : 'text-text-muted hover:text-text-secondary hover:bg-surface-hover/50'
                              }`}
                              title="Historia cen"
                            >
                              <Clock className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </td>
                      </tr>
                      {isHistoryExpanded && p.id && (
                        <tr>
                          <td colSpan={5} className="p-0">
                            <PriceHistoryPanel
                              parentId={p.id}
                              costType="product"
                              storeId={p.storeId ?? null}
                              onPriceUpdated={load}
                            />
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between pt-2 text-sm text-text-secondary">
              <span>
                Strona {page} z {totalPages} ({total} produktów)
              </span>
              <div className="flex gap-2">
                <button
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  className="px-3 py-1.5 rounded-lg bg-surface-hover/50 hover:bg-surface-hover disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                >
                  Poprzednia
                </button>
                <button
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => p + 1)}
                  className="px-3 py-1.5 rounded-lg bg-surface-hover/50 hover:bg-surface-hover disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                >
                  Następna
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Save */}
      {products.length > 0 && (
        <div className="flex justify-end pt-2">
          <button onClick={handleSave} disabled={saving} className="btn-primary">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Zapisz
          </button>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Progi wagowe kosztu wysyłki (panel pod metodą)
// ---------------------------------------------------------------------------

function WeightTiersPanel({
  shippingCostId,
  tiers,
  isVat,
  onSaved,
}: {
  shippingCostId: string;
  tiers: ShippingWeightTier[];
  isVat: boolean;
  onSaved: () => void;
}) {
  const [rows, setRows] = useState<ShippingWeightTier[]>(tiers);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const updateRow = (idx: number, patch: Partial<ShippingWeightTier>) => {
    setRows((prev) => prev.map((row, i) => (i === idx ? { ...row, ...patch } : row)));
  };

  const handleSave = async () => {
    setSaving(true);
    setFeedback(null);
    try {
      await financeApi.saveShippingCostTiers(shippingCostId, rows);
      setFeedback({ type: 'success', message: 'Progi wagowe zostały zapisane' });
      onSaved();
    } catch (err: unknown) {
      const issues = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      setFeedback({ type: 'error', message: issues || 'Nie udało się zapisać progów wagowych' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-surface-hover/20 border-t border-border-subtle px-4 py-3 space-y-3">
      {feedback && <Feedback type={feedback.type} message={feedback.message} />}
      <p className="text-xs text-text-muted">
        Cena netto zależna od wagi paczki (suma wag pozycji zamówienia). Przedział obejmuje wagę od
        (włącznie) do (wyłącznie); puste „do" oznacza brak górnej granicy. Gdy waga jest nieznana
        albo żaden próg nie pasuje, obowiązuje stawka podstawowa metody.
      </p>
      {rows.map((row, idx) => (
        <div key={idx} className="flex items-center gap-2 text-sm">
          <span className="text-xs text-text-muted">od</span>
          <input
            type="number"
            step="0.01"
            min="0"
            value={row.weightFromKg}
            onChange={(e) => updateRow(idx, { weightFromKg: parseFloat(e.target.value) || 0 })}
            className="input w-24 text-right text-sm"
            aria-label="Waga od (kg)"
          />
          <span className="text-xs text-text-muted">do</span>
          <input
            type="number"
            step="0.01"
            min="0"
            value={row.weightToKg ?? ''}
            placeholder="bez góry"
            onChange={(e) => updateRow(idx, { weightToKg: e.target.value === '' ? null : (parseFloat(e.target.value) || 0) })}
            className="input w-24 text-right text-sm"
            aria-label="Waga do (kg)"
          />
          <span className="text-xs text-text-muted">kg</span>
          <input
            type="number"
            step="0.01"
            min="0"
            value={row.costNet}
            onChange={(e) => updateRow(idx, { costNet: parseFloat(e.target.value) || 0 })}
            className="input w-28 text-right text-sm"
            aria-label="Koszt netto progu"
          />
          <span className="text-xs text-text-muted">PLN {isVat ? 'netto' : 'brutto'}</span>
          <button
            type="button"
            onClick={() => setRows((prev) => prev.filter((_, i) => i !== idx))}
            className="p-1.5 rounded-lg text-text-muted hover:text-danger hover:bg-danger/10 transition-colors"
            aria-label="Usuń próg"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      ))}
      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={() => setRows((prev) => [...prev, { weightFromKg: prev.length ? (prev[prev.length - 1].weightToKg ?? 0) : 0, weightToKg: null, costNet: 0 }])}
          className="text-xs text-accent-primary hover:text-accent-primary/80 flex items-center gap-1 transition-colors"
        >
          <Plus className="w-3 h-3" />
          Dodaj próg
        </button>
        <button onClick={handleSave} disabled={saving} className="btn-primary text-sm">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Zapisz progi
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Section 2 -- Koszty wysyłki
// ---------------------------------------------------------------------------

function ShippingCostsSection({ isVat, storeId }: { isVat: boolean; storeId?: string }) {
  const [methods, setMethods] = useState<ShippingMethod[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [expandedHistoryKey, setExpandedHistoryKey] = useState<string | null>(null);
  const [expandedTiersKey, setExpandedTiersKey] = useState<string | null>(null);

  const loadMethods = useCallback(async () => {
    try {
      const { data } = await financeApi.getShippingCosts(storeId ? { storeId } : undefined);
      const res = data as unknown as { methods: ShippingMethod[] };
      setMethods(res.methods ?? []);
    } catch {
      setFeedback({ type: 'error', message: 'Nie udało się wczytać metod wysyłki' });
    } finally {
      setLoading(false);
    }
  }, [storeId]);

  useEffect(() => {
    loadMethods();
  }, [loadMethods]);

  const toggleEnabled = (idx: number) => {
    setMethods((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], isEnabled: !next[idx].isEnabled };
      return next;
    });
  };

  const handleCostChange = (idx: number, value: string) => {
    setMethods((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], costNet: parseFloat(value) || 0 };
      return next;
    });
  };

  const handleReturnCostChange = (idx: number, value: string) => {
    setMethods((prev) => {
      const next = [...prev];
      // Puste pole = brak naliczania kosztu zwrotu nieodebranej paczki.
      next[idx] = { ...next[idx], returnCostNet: value === '' ? null : (parseFloat(value) || 0) };
      return next;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    setFeedback(null);
    try {
      await financeApi.saveShippingCosts(
        methods.map((m) => ({
          shippingMethodKey: m.shippingMethodKey,
          shippingMethodName: m.shippingMethodName,
          costNet: m.costNet,
          returnCostNet: m.returnCostNet ?? null,
          isEnabled: m.isEnabled,
        })),
        storeId,
      );
      setFeedback({ type: 'success', message: 'Koszty wysyłki zostały zapisane' });
      // Odswiezenie po zapisie: nowe rekordy dostaja prawdziwe id (progi wagowe).
      await loadMethods();
    } catch {
      setFeedback({ type: 'error', message: 'Nie udało się zapisać kosztów wysyłki' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-accent-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {feedback && <Feedback type={feedback.type} message={feedback.message} />}

      {methods.length === 0 ? (
        <EmptyState
          icon={Truck}
          title="Brak metod wysyłki"
          description="Zsynchronizuj zamówienia, aby pobrać metody."
          size="sm"
        />
      ) : (
        <div className="space-y-3">
          {methods.map((m, idx) => {
            const isHistoryExpanded = expandedHistoryKey === m.shippingMethodKey;
            const isTiersExpanded = expandedTiersKey === m.shippingMethodKey;
            const tiersCount = m.weightTiers?.length ?? 0;
            return (
              <div key={m.shippingMethodKey} className="rounded-xl overflow-hidden">
                <div
                  className={`flex items-center gap-4 p-4 transition-colors ${
                    m.isEnabled ? 'bg-surface-hover/30' : 'bg-surface-hover/10 opacity-60'
                  }`}
                >
                  {/* Toggle */}
                  <button
                    type="button"
                    onClick={() => toggleEnabled(idx)}
                    className={`relative w-10 h-5 rounded-full flex-shrink-0 transition-colors ${
                      m.isEnabled ? 'bg-success' : 'bg-surface-muted'
                    }`}
                  >
                    <div
                      className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${
                        m.isEnabled ? 'left-5' : 'left-0.5'
                      }`}
                    />
                  </button>

                  {/* Name */}
                  <div className="flex-1 min-w-0">
                    <span className="text-text-primary text-sm font-medium truncate block">{m.shippingMethodName}</span>
                    {tiersCount > 0 && (
                      <span className="text-[11px] text-accent-primary">{tiersCount} progi wagowe</span>
                    )}
                  </div>

                  {/* Cost input */}
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={m.costNet || ''}
                      onChange={(e) => handleCostChange(idx, e.target.value)}
                      className="input w-28 text-right text-sm"
                      placeholder="0.00"
                      aria-label="Koszt wysyłki"
                    />
                    <span className="text-xs text-text-muted">PLN {isVat ? 'netto' : 'brutto'}</span>
                  </div>

                  {/* Return cost input (nieodebrana paczka) */}
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={m.returnCostNet ?? ''}
                      onChange={(e) => handleReturnCostChange(idx, e.target.value)}
                      className="input w-24 text-right text-sm"
                      placeholder="brak"
                      aria-label="Koszt zwrotu paczki"
                      title="Koszt zwrotu nieodebranej paczki (netto). Puste = brak naliczania."
                    />
                    <span className="text-xs text-text-muted">zwrot</span>
                  </div>

                  {/* Weight tiers toggle */}
                  {m.id && (
                    <button
                      onClick={() => setExpandedTiersKey(isTiersExpanded ? null : m.shippingMethodKey)}
                      className={`p-1.5 rounded-lg transition-colors flex-shrink-0 ${
                        isTiersExpanded
                          ? 'text-accent-primary bg-accent-primary/10'
                          : tiersCount > 0
                            ? 'text-accent-primary hover:bg-accent-primary/10'
                            : 'text-text-muted hover:text-text-secondary hover:bg-surface-hover/50'
                      }`}
                      title="Progi wagowe"
                    >
                      <Scale className="w-3.5 h-3.5" />
                    </button>
                  )}

                  {/* History toggle */}
                  {m.id && (
                    <button
                      onClick={() => setExpandedHistoryKey(isHistoryExpanded ? null : m.shippingMethodKey)}
                      className={`p-1.5 rounded-lg transition-colors flex-shrink-0 ${
                        isHistoryExpanded
                          ? 'text-accent-primary bg-accent-primary/10'
                          : (m.historyCount ?? 0) > 0
                            ? 'text-accent-primary hover:bg-accent-primary/10'
                            : 'text-text-muted hover:text-text-secondary hover:bg-surface-hover/50'
                      }`}
                      title="Historia cen"
                    >
                      <Clock className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
                {isTiersExpanded && m.id && (
                  <WeightTiersPanel
                    shippingCostId={m.id}
                    tiers={m.weightTiers ?? []}
                    isVat={isVat}
                    onSaved={loadMethods}
                  />
                )}
                {isHistoryExpanded && m.id && (
                  <PriceHistoryPanel
                    parentId={m.id}
                    costType="shipping"
                    storeId={m.storeId ?? null}
                    onPriceUpdated={loadMethods}
                  />
                )}
              </div>
            );
          })}
        </div>
      )}

      {methods.length > 0 && (
        <div className="flex justify-end pt-2">
          <button onClick={handleSave} disabled={saving} className="btn-primary">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Zapisz
          </button>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Section 3 -- Prowizje transakcyjne
// ---------------------------------------------------------------------------

function TransactionFeesSection({ storeId }: { storeId?: string }) {
  const confirmDialog = useConfirmDialog();
  const [fees, setFees] = useState<TransactionFee[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [expandedHistoryId, setExpandedHistoryId] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const { data } = await financeApi.getTransactionFees(storeId ? { storeId } : undefined);
      const res = data as unknown as { fees: TransactionFee[] };
      setFees(res.fees ?? []);
    } catch {
      setFeedback({ type: 'error', message: 'Nie udało się wczytać prowizji' });
    } finally {
      setLoading(false);
    }
  }, [storeId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleAdd = async () => {
    setFeedback(null);
    try {
      const { data } = await financeApi.createTransactionFee({
        name: 'Nowa prowizja',
        paymentMethod: null,
        percentFee: 0,
        fixedFee: 0,
        chargebackFee: 0,
        storeId,
      });
      const res = data as unknown as { fee: TransactionFee };
      if (res.fee) {
        setFees((prev) => [...prev, res.fee]);
      } else {
        await load();
      }
    } catch {
      setFeedback({ type: 'error', message: 'Nie udało się dodać prowizji' });
    }
  };

  const handleUpdate = async (fee: TransactionFee) => {
    setSaving(fee.id);
    setFeedback(null);
    try {
      await financeApi.updateTransactionFee(fee.id, {
        name: fee.name,
        paymentMethod: fee.paymentMethod,
        percentFee: fee.percentFee,
        fixedFee: fee.fixedFee,
        chargebackFee: fee.chargebackFee ?? 0,
      });
      setFeedback({ type: 'success', message: 'Prowizja zaktualizowana' });
    } catch {
      setFeedback({ type: 'error', message: 'Nie udało się zaktualizować prowizji' });
    } finally {
      setSaving(null);
    }
  };

  const handleDelete = async (id: string) => {
    if (!(await confirmDialog({
      title: 'Usuń prowizję',
      message: 'Usunąć tę prowizję?',
      confirmLabel: 'Usuń',
    }))) return;
    setFeedback(null);
    try {
      await financeApi.deleteTransactionFee(id);
      setFees((prev) => prev.filter((f) => f.id !== id));
      setFeedback({ type: 'success', message: 'Prowizja usunięta' });
    } catch {
      setFeedback({ type: 'error', message: 'Nie udało się usunąć prowizji' });
    }
  };

  const handleFieldChange = (id: string, field: keyof TransactionFee, value: string | number | null) => {
    setFees((prev) =>
      prev.map((f) => (f.id === id ? { ...f, [field]: value } : f)),
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-accent-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 p-3 rounded-xl text-xs bg-info/10 border border-info/20 text-accent-info">
        <Info className="w-4 h-4 flex-shrink-0" />
        Prowizje są naliczane od kwoty brutto zamówienia (łączna wartość, którą płaci klient).
      </div>
      {feedback && <Feedback type={feedback.type} message={feedback.message} />}

      {fees.length === 0 ? (
        <EmptyState
          icon={CreditCard}
          title="Brak prowizji transakcyjnych"
          size="sm"
        />
      ) : (
        <div className="space-y-3">
          {fees.map((fee) => {
            const isHistoryExpanded = expandedHistoryId === fee.id;
            return (
              <div key={fee.id} className="bg-surface-hover/30 rounded-xl overflow-hidden">
                <div className="p-4 space-y-3">
                  <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
                    {/* Name */}
                    <div>
                      <label className="text-xs text-text-muted mb-1 block">Nazwa</label>
                      <input
                        type="text"
                        value={fee.name}
                        onChange={(e) => handleFieldChange(fee.id, 'name', e.target.value)}
                        className="input text-sm"
                        placeholder="np. Stripe"
                      />
                    </div>
                    {/* Percent fee */}
                    <div>
                      <label className="text-xs text-text-muted mb-1 block">Prowizja %</label>
                      <DecimalInput
                        value={fee.percentFee}
                        onChange={(v) => handleFieldChange(fee.id, 'percentFee', v)}
                        placeholder="0.85"
                      />
                    </div>
                    {/* Fixed fee */}
                    <div>
                      <label className="text-xs text-text-muted mb-1 block">Stała opłata (PLN brutto)</label>
                      <DecimalInput
                        value={fee.fixedFee}
                        onChange={(v) => handleFieldChange(fee.id, 'fixedFee', v)}
                        placeholder="0.00"
                      />
                    </div>
                    {/* Chargeback fee */}
                    <div>
                      <label className="text-xs text-text-muted mb-1 block">Opłata za chargeback (PLN, domyślnie 0)</label>
                      <DecimalInput
                        value={fee.chargebackFee ?? 0}
                        onChange={(v) => handleFieldChange(fee.id, 'chargebackFee', v)}
                        placeholder="0.00"
                      />
                    </div>
                  </div>
                  <div className="flex items-center justify-end gap-2">
                    <button
                      onClick={() => setExpandedHistoryId(isHistoryExpanded ? null : fee.id)}
                      className={`p-1.5 rounded-lg transition-colors ${
                        isHistoryExpanded
                          ? 'text-accent-primary bg-accent-primary/10'
                          : (fee.historyCount ?? 0) > 0
                            ? 'text-accent-primary hover:bg-accent-primary/10'
                            : 'text-text-muted hover:text-text-secondary hover:bg-surface-hover/50'
                      }`}
                      title="Historia cen"
                    >
                      <Clock className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => handleUpdate(fee)}
                      disabled={saving === fee.id}
                      className="px-3 py-1.5 rounded-lg text-xs font-medium bg-accent-primary/10 text-accent-primary hover:bg-accent-primary/20 transition-colors"
                    >
                      {saving === fee.id ? (
                        <Loader2 className="w-3 h-3 animate-spin inline mr-1" />
                      ) : (
                        <Save className="w-3 h-3 inline mr-1" />
                      )}
                      Zapisz
                    </button>
                    <button
                      onClick={() => handleDelete(fee.id)}
                      className="px-3 py-1.5 rounded-lg text-xs font-medium text-danger hover:bg-danger/10 transition-colors"
                    >
                      <Trash2 className="w-3 h-3 inline mr-1" />
                      Usuń
                    </button>
                  </div>
                </div>
                {isHistoryExpanded && (
                  <PriceHistoryPanel
                    parentId={fee.id}
                    costType="fee"
                    storeId={fee.storeId ?? null}
                    onPriceUpdated={load}
                  />
                )}
              </div>
            );
          })}
        </div>
      )}

      <div className="flex justify-start pt-2">
        <button onClick={handleAdd} className="btn-secondary text-sm">
          <Plus className="w-4 h-4 mr-2" />
          Dodaj prowizję
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Section 4 -- Koszty stałe miesięczne
// ---------------------------------------------------------------------------

function FixedCostsSection({ isVat, storeId }: { isVat: boolean; storeId?: string }) {
  const confirmDialog = useConfirmDialog();
  const [costs, setCosts] = useState<FixedCost[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [expandedHistoryId, setExpandedHistoryId] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const { data } = await financeApi.getFixedCosts(storeId ? { storeId } : undefined);
      const res = data as unknown as { costs: FixedCost[] };
      setCosts(res.costs ?? []);
    } catch {
      setFeedback({ type: 'error', message: 'Nie udało się wczytać kosztów stałych' });
    } finally {
      setLoading(false);
    }
  }, [storeId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleAdd = async () => {
    setFeedback(null);
    try {
      const { data } = await financeApi.createFixedCost({
        name: 'Nowy koszt',
        category: 'other',
        amountNet: 0,
        storeId,
      });
      const res = data as unknown as { cost: FixedCost };
      if (res.cost) {
        setCosts((prev) => [...prev, res.cost]);
      } else {
        await load();
      }
    } catch {
      setFeedback({ type: 'error', message: 'Nie udało się dodać kosztu' });
    }
  };

  const handleUpdate = async (cost: FixedCost) => {
    setSaving(cost.id);
    setFeedback(null);
    try {
      await financeApi.updateFixedCost(cost.id, {
        name: cost.name,
        category: cost.category,
        amountNet: cost.amountNet,
      });
      setFeedback({ type: 'success', message: 'Koszt zaktualizowany' });
    } catch {
      setFeedback({ type: 'error', message: 'Nie udało się zaktualizować kosztu' });
    } finally {
      setSaving(null);
    }
  };

  const handleDelete = async (id: string) => {
    if (!(await confirmDialog({
      title: 'Usuń koszt',
      message: 'Usunąć ten koszt?',
      confirmLabel: 'Usuń',
    }))) return;
    setFeedback(null);
    try {
      await financeApi.deleteFixedCost(id);
      setCosts((prev) => prev.filter((c) => c.id !== id));
      setFeedback({ type: 'success', message: 'Koszt usunięty' });
    } catch {
      setFeedback({ type: 'error', message: 'Nie udało się usunąć kosztu' });
    }
  };

  const handleFieldChange = (id: string, field: keyof FixedCost, value: string | number) => {
    setCosts((prev) =>
      prev.map((c) => (c.id === id ? { ...c, [field]: value } : c)),
    );
  };

  const totalMonthly = costs.reduce((sum, c) => sum + (c.amountNet || 0), 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-accent-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {feedback && <Feedback type={feedback.type} message={feedback.message} />}

      {costs.length === 0 ? (
        <EmptyState
          icon={Building2}
          title="Brak kosztów stałych"
          size="sm"
        />
      ) : (
        <div className="space-y-3">
          {costs.map((cost) => {
            const isHistoryExpanded = expandedHistoryId === cost.id;
            return (
              <div key={cost.id} className="bg-surface-hover/30 rounded-xl overflow-hidden">
                <div className="p-4 space-y-3">
                  <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
                    {/* Name */}
                    <div>
                      <label className="text-xs text-text-muted mb-1 block">Nazwa</label>
                      <input
                        type="text"
                        value={cost.name}
                        onChange={(e) => handleFieldChange(cost.id, 'name', e.target.value)}
                        className="input text-sm"
                        placeholder="np. Hosting"
                      />
                    </div>
                    {/* Category */}
                    <div>
                      <label className="text-xs text-text-muted mb-1 block">Kategoria</label>
                      <select
                        value={cost.category}
                        onChange={(e) => handleFieldChange(cost.id, 'category', e.target.value)}
                        className="input text-sm"
                      >
                        {FIXED_COST_CATEGORIES.map((cat) => (
                          <option key={cat.value} value={cat.value}>
                            {cat.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    {/* Amount */}
                    <div>
                      <label className="text-xs text-text-muted mb-1 block">Kwota {isVat ? 'netto' : 'brutto'} / mies.</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={cost.amountNet || ''}
                        onChange={(e) => handleFieldChange(cost.id, 'amountNet', parseFloat(e.target.value) || 0)}
                        className="input text-sm"
                        placeholder="0.00"
                      />
                    </div>
                  </div>
                  <div className="flex items-center justify-end gap-2">
                    <button
                      onClick={() => setExpandedHistoryId(isHistoryExpanded ? null : cost.id)}
                      className={`p-1.5 rounded-lg transition-colors ${
                        isHistoryExpanded
                          ? 'text-accent-primary bg-accent-primary/10'
                          : (cost.historyCount ?? 0) > 0
                            ? 'text-accent-primary hover:bg-accent-primary/10'
                            : 'text-text-muted hover:text-text-secondary hover:bg-surface-hover/50'
                      }`}
                      title="Historia cen"
                    >
                      <Clock className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => handleUpdate(cost)}
                      disabled={saving === cost.id}
                      className="px-3 py-1.5 rounded-lg text-xs font-medium bg-accent-primary/10 text-accent-primary hover:bg-accent-primary/20 transition-colors"
                    >
                      {saving === cost.id ? (
                        <Loader2 className="w-3 h-3 animate-spin inline mr-1" />
                      ) : (
                        <Save className="w-3 h-3 inline mr-1" />
                      )}
                      Zapisz
                    </button>
                    <button
                      onClick={() => handleDelete(cost.id)}
                      className="px-3 py-1.5 rounded-lg text-xs font-medium text-danger hover:bg-danger/10 transition-colors"
                    >
                      <Trash2 className="w-3 h-3 inline mr-1" />
                      Usuń
                    </button>
                  </div>
                </div>
                {isHistoryExpanded && (
                  <PriceHistoryPanel
                    parentId={cost.id}
                    costType="fixed"
                    storeId={cost.storeId ?? null}
                    onPriceUpdated={load}
                  />
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Total */}
      {costs.length > 0 && (
        <div className="flex items-center justify-between p-4 bg-surface-hover/50 rounded-xl border border-border-subtle">
          <span className="text-text-secondary text-sm font-medium">Suma kosztów stałych miesięcznie:</span>
          <span className="text-text-primary font-bold text-lg">{formatPLN(totalMonthly)}</span>
        </div>
      )}

      <div className="flex justify-start pt-2">
        <button onClick={handleAdd} className="btn-secondary text-sm">
          <Plus className="w-4 h-4 mr-2" />
          Dodaj koszt stały
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Section 5 -- Powiązanie produktów z reklamami
// ---------------------------------------------------------------------------

interface AdMapping {
  storeId?: string | null;
  id: string;
  externalProductId: string;
  accountId: string;
  useWholeAccount: boolean;
  campaignIds: string[];
  account?: { accountId: string; accountName: string };
}

interface FbAccount {
  id: string;
  accountId: string;
  accountName: string;
  storeId: string | null;
}

interface Campaign {
  id: string;
  name: string;
  status: string;
}

function AdMappingsSection({ storeId }: { storeId?: string }) {
  const confirmDialog = useConfirmDialog();
  const [unassignedMappingCount, setUnassignedMappingCount] = useState(0);
  const [mappings, setMappings] = useState<AdMapping[]>([]);
  const [fbAccounts, setFbAccounts] = useState<FbAccount[]>([]);
  const [products, setProducts] = useState<ProductCost[]>([]);
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [loading, setLoading] = useState(true);
  const [bulkConnecting, setBulkConnecting] = useState(false);
  const [bulkAccountId, setBulkAccountId] = useState('');

  // Dialog state
  const [showDialog, setShowDialog] = useState(false);
  const [editMapping, setEditMapping] = useState<AdMapping | null>(null);
  const [selectedProductIds, setSelectedProductIds] = useState<string[]>([]);
  const [selectedAccountDbId, setSelectedAccountDbId] = useState('');
  const [selectedCampaignIds, setSelectedCampaignIds] = useState<string[]>([]);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loadingCampaigns, setLoadingCampaigns] = useState(false);
  const [savingMapping, setSavingMapping] = useState(false);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [mappingsRes, accountsRes, productsRes] = await Promise.all([
        financeApi.getAdMappings(storeId),
        financeApi.getFacebookAccounts(),
        financeApi.getProductCosts({ limit: 500, storeId }),
      ]);
      const m = mappingsRes.data;
      const a = accountsRes.data;
      const p = productsRes.data;
      const allMappings: AdMapping[] = Array.isArray(m) ? m : [];
      setFbAccounts(Array.isArray(a) ? a : []);
      setUnassignedMappingCount(allMappings.filter(mapping => !mapping.storeId).length);
      const allProducts: ProductCost[] = p?.products || p?.data || (Array.isArray(p) ? p : []);
      // Filter products by storeId if provided
      const filteredProducts = storeId
        ? allProducts.filter((pr: any) => pr.storeId === storeId)
        : allProducts;
      setProducts(filteredProducts);
      // Filter mappings to only those matching filtered products
      const filteredProductIds = new Set(filteredProducts.map(pr => pr.externalProductId));
      setMappings(storeId
        ? allMappings.filter(m => m.storeId === storeId && filteredProductIds.has(m.externalProductId))
        : allMappings
      );
    } catch {
      setFeedback({ type: 'error', message: 'Nie udało się wczytać powiązań z reklamami' });
    } finally {
      setLoading(false);
    }
  }, [storeId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Check if all products in this store are bulk-connected to the same account
  const isBulkConnected = useMemo(() => {
    if (products.length === 0) return false;
    if (mappings.length === 0) return false;
    const allProductIds = products.map(p => p.externalProductId);
    const mappedIds = new Set(mappings.map(m => m.externalProductId));
    const allMapped = allProductIds.every(id => mappedIds.has(id));
    if (!allMapped) return false;
    const allWholeAccount = mappings.every(m => m.useWholeAccount);
    if (!allWholeAccount) return false;
    const accountIds = new Set(mappings.map(m => m.accountId));
    return accountIds.size === 1;
  }, [products, mappings]);

  const bulkConnectedAccountId = isBulkConnected && mappings.length > 0 ? mappings[0].accountId : null;

  // Handle bulk connect all products to a single account
  const handleBulkConnect = async () => {
    if (!bulkAccountId || products.length === 0) return;
    try {
      setBulkConnecting(true);
      setFeedback(null);
      await financeApi.bulkSaveAdMappings(
        bulkAccountId,
        products.map(p => p.externalProductId),
        storeId,
      );
      setFeedback({ type: 'success', message: 'Wszystkie produkty połączone z kontem reklamowym' });
      await loadData();
    } catch {
      setFeedback({ type: 'error', message: 'Nie udało się połączyć produktów' });
    } finally {
      setBulkConnecting(false);
    }
  };

  // Handle disconnect all (delete all mappings for this store's products)
  const handleBulkDisconnect = async () => {
    if (!(await confirmDialog({
      title: 'Odłącz produkty',
      message: 'Odłączyć wszystkie produkty od konta reklamowego?',
      confirmLabel: 'Odłącz',
    }))) return;
    try {
      setBulkConnecting(true);
      for (const m of mappings) {
        await financeApi.deleteAdMapping(m.id);
      }
      setFeedback({ type: 'success', message: 'Powiązania usunięte' });
      await loadData();
    } catch {
      setFeedback({ type: 'error', message: 'Nie udało się usunąć powiązań' });
    } finally {
      setBulkConnecting(false);
    }
  };

  const openNewDialog = () => {
    setEditMapping(null);
    setSelectedProductIds([]);
    setSelectedAccountDbId('');
    setSelectedCampaignIds([]);
    setCampaigns([]);
    setShowDialog(true);
  };

  const openEditGroup = (group: { accountId: string; campaignIds: string[]; productIds: string[]; mappingIds: string[] }) => {
    setEditMapping(null);
    setSelectedProductIds([...group.productIds]);
    setSelectedAccountDbId(group.accountId);
    setSelectedCampaignIds([...group.campaignIds]);
    setCampaigns([]);
    setShowDialog(true);
    loadCampaigns(group.accountId);
  };

  const handleDeleteGroup = async (mappingIds: string[]) => {
    if (!(await confirmDialog({
      title: 'Usuń grupę powiązań',
      message: 'Usunąć tę grupę powiązań?',
      confirmLabel: 'Usuń',
    }))) return;
    try {
      for (const id of mappingIds) {
        await financeApi.deleteAdMapping(id);
      }
      setFeedback({ type: 'success', message: 'Powiązania usunięte' });
      await loadData();
    } catch {
      setFeedback({ type: 'error', message: 'Nie udało się usunąć powiązań' });
    }
  };

  const loadCampaigns = async (accountDbId: string) => {
    if (!accountDbId) return;
    try {
      setLoadingCampaigns(true);
      const res = await financeApi.getFacebookCampaigns(accountDbId);
      setCampaigns(Array.isArray(res.data) ? res.data : []);
    } catch {
      setCampaigns([]);
    } finally {
      setLoadingCampaigns(false);
    }
  };

  const handleAccountChange = (accountDbId: string) => {
    setSelectedAccountDbId(accountDbId);
    setCampaigns([]);
    setSelectedCampaignIds([]);
    if (accountDbId) {
      loadCampaigns(accountDbId);
    }
  };

  const handleSaveMapping = async () => {
    if (selectedProductIds.length === 0 || !selectedAccountDbId || selectedCampaignIds.length === 0) return;
    try {
      setSavingMapping(true);
      setFeedback(null);

      // When editing, remove mappings for products that were deselected
      const existingMappedIds = new Set(
        mappings
          .filter(m => m.accountId === selectedAccountDbId && JSON.stringify([...(m.campaignIds || [])].sort()) === JSON.stringify([...selectedCampaignIds].sort()))
          .map(m => m.externalProductId)
      );
      for (const m of mappings) {
        if (existingMappedIds.has(m.externalProductId) && !selectedProductIds.includes(m.externalProductId)) {
          await financeApi.deleteAdMapping(m.id);
        }
      }

      // Save/update all selected products
      for (const pid of selectedProductIds) {
        await financeApi.saveAdMapping({
          storeId,
          externalProductId: pid,
          accountId: selectedAccountDbId,
          useWholeAccount: false,
          campaignIds: selectedCampaignIds,
        });
      }
      setShowDialog(false);
      setFeedback({ type: 'success', message: `Powiązano ${selectedProductIds.length} produktów z ${selectedCampaignIds.length} kampaniami` });
      await loadData();
    } catch {
      setFeedback({ type: 'error', message: 'Nie udało się zapisać powiązania' });
    } finally {
      setSavingMapping(false);
    }
  };

  const toggleCampaign = (campaignId: string) => {
    setSelectedCampaignIds(prev =>
      prev.includes(campaignId)
        ? prev.filter(id => id !== campaignId)
        : [...prev, campaignId]
    );
  };

  const getProductName = (externalId: string) => {
    const p = products.find(pr => pr.externalProductId === externalId);
    return p?.productName || externalId;
  };

  const getAccountName = (mapping: AdMapping) => {
    return mapping.account?.accountName || mapping.accountId;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-accent-primary" />
      </div>
    );
  }

  if (fbAccounts.length === 0) {
    return (
      <EmptyState
        icon={Megaphone}
        title="Brak połączonego konta reklamowego"
        description="Najpierw połącz konto reklamowe Facebook w zakładce Facebook."
        size="sm"
      />
    );
  }

  return (
    <div className="space-y-4">
      {unassignedMappingCount > 0 && <p className="rounded-lg border border-warning-border bg-warning-bg p-3 text-xs text-warning">{unassignedMappingCount} historycznych powiązań reklam nie ma ustalonego sklepu. Przypisz kampanię do produktu w odpowiednim sklepie; niejednoznaczne powiązania nie są używane w wyniku produktu.</p>}
      {/* Feedback */}
      {feedback && (
        <div className={`flex items-center gap-2 p-3 rounded-xl text-sm ${
          feedback.type === 'success' ? 'bg-success/10 text-success border border-success/20' : 'bg-danger/10 text-danger border border-danger/20'
        }`}>
          {feedback.type === 'success' ? <CheckCircle className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
          {feedback.message}
        </div>
      )}

      {/* Info note */}
      <div className="p-3 rounded-xl bg-blue-500/5 border border-blue-500/15 flex gap-2">
        <Info className="w-4 h-4 text-accent-info flex-shrink-0 mt-0.5" />
        <p className="text-xs text-text-secondary leading-relaxed">
          <span className="text-accent-info font-medium">Powiązanie globalne</span> — przypisuje koszty z całego konta reklamowego do wszystkich produktów w sklepie. Używaj gdy cały budżet reklamowy dotyczy jednego produktu (One Product Shop).
          {' '}<span className="text-accent-info font-medium">Powiązanie z kampanią</span> — pozwala przypisać grupę produktów do konkretnej kampanii. Koszty tej kampanii będą rozliczone tylko dla powiązanych produktów. Używaj gdy masz osobne kampanie pod różne produkty w jednym sklepie internetowym.
        </p>
      </div>

      {/* Bulk connect / disconnect button */}
      {isBulkConnected ? (
        <div className="p-4 rounded-xl bg-green-500/5 border border-success/20">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-success" />
              <div>
                <p className="text-sm text-success font-medium">
                  Wszystkie produkty ({products.length}) połączone z kontem
                </p>
                <p className="text-xs text-text-muted mt-0.5">
                  {fbAccounts.find(a => a.id === bulkConnectedAccountId)?.accountName || 'Konto reklamowe'}
                  {' '}— Całe konto (globalnie)
                </p>
              </div>
            </div>
            <button
              onClick={handleBulkDisconnect}
              disabled={bulkConnecting}
              className="px-3 py-1.5 text-xs text-danger border border-danger/20 rounded-lg hover:bg-danger/10 transition-colors disabled:opacity-50"
            >
              {bulkConnecting ? 'Odłączanie...' : 'Odłącz wszystko'}
            </button>
          </div>
        </div>
      ) : (
        <div className="p-4 rounded-xl bg-surface-hover/30 border border-border-subtle">
          <p className="text-sm text-text-secondary mb-3">
            Połącz wszystkie produkty z kontem reklamowym (globalnie, bez podziału na kampanie):
          </p>
          <div className="flex items-center gap-3">
            <select
              value={bulkAccountId}
              onChange={(e) => setBulkAccountId(e.target.value)}
              className="input flex-1 text-sm"
            >
              <option value="">— Wybierz konto reklamowe —</option>
              {fbAccounts.map((acc) => (
                <option key={acc.id} value={acc.id}>
                  {acc.accountName || acc.accountId}
                </option>
              ))}
            </select>
            <button
              onClick={handleBulkConnect}
              disabled={bulkConnecting || !bulkAccountId || products.length === 0}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-text-primary bg-purple-500/20 border border-purple-500/30 hover:bg-purple-500/30 transition-colors disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap"
            >
              {bulkConnecting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <LinkIcon className="w-4 h-4" />
              )}
              Połącz wszystkie ({products.length})
            </button>
          </div>
        </div>
      )}

      {/* Campaign-grouped mappings list — only shown when NOT bulk connected */}
      {!isBulkConnected && (
        <>
          {mappings.length === 0 ? (
            <EmptyState
              icon={LinkIcon}
              title="Brak powiązań produktów z kontami reklamowymi"
              size="sm"
            />
          ) : (
            <div className="space-y-2">
              {(() => {
                // Group mappings by accountId + campaignIds key
                const groups: { key: string; accountId: string; accountName: string; campaignIds: string[]; productIds: string[]; mappingIds: string[] }[] = [];
                const groupMap = new Map<string, typeof groups[0]>();
                for (const m of mappings) {
                  const cids = [...(m.campaignIds || [])].sort();
                  const key = `${m.accountId}::${cids.join(',')}`;
                  let g = groupMap.get(key);
                  if (!g) {
                    g = { key, accountId: m.accountId, accountName: getAccountName(m), campaignIds: cids, productIds: [], mappingIds: [] };
                    groupMap.set(key, g);
                    groups.push(g);
                  }
                  g.productIds.push(m.externalProductId);
                  g.mappingIds.push(m.id);
                }
                return groups.map((group) => (
                  <div key={group.key} className="p-3 rounded-xl bg-surface-base/30 border border-border-subtle hover:border-border transition-colors">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-text-muted">
                          {group.accountName} — {group.campaignIds.length > 0 ? `${group.campaignIds.length} kampanii` : 'Brak kampanii'}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => openEditGroup(group)}
                          className="text-xs text-text-secondary hover:text-accent-primary px-2 py-1 rounded border border-border-subtle hover:border-accent-primary/30 transition-colors"
                        >
                          Edytuj
                        </button>
                        <button
                          onClick={() => handleDeleteGroup(group.mappingIds)}
                          className="p-1.5 text-text-muted hover:text-danger hover:bg-danger/10 rounded-lg transition-colors"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {group.productIds.map((pid) => (
                        <span key={pid} className="px-2 py-1 text-xs bg-purple-500/10 text-purple-300 rounded-lg border border-purple-500/15">
                          {getProductName(pid)}
                        </span>
                      ))}
                    </div>
                  </div>
                ));
              })()}
            </div>
          )}

          <div className="flex justify-start pt-2">
            <button onClick={openNewDialog} className="btn-secondary text-sm">
              <Plus className="w-4 h-4 mr-2" />
              Dodaj powiązanie
            </button>
          </div>
        </>
      )}

      {/* Ad Mapping Dialog */}
      {showDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="bg-surface-raised rounded-2xl border border-border shadow-2xl w-full max-w-lg mx-4">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-border-subtle">
              <h2 className="text-lg font-semibold text-text-primary">
                {editMapping ? 'Edytuj powiązanie' : 'Powiązanie z reklamami'}
              </h2>
              <button
                onClick={() => setShowDialog(false)}
                className="p-1.5 rounded-lg text-text-secondary hover:text-text-primary hover:bg-surface-hover/50 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Body */}
            <div className="px-6 py-4 space-y-4">
              {/* Product multi-select */}
              <div>
                <label className="block text-sm font-medium text-text-secondary mb-1.5">
                  Produkty {selectedProductIds.length > 0 && <span className="text-accent-primary">({selectedProductIds.length})</span>}
                </label>
                <div className="max-h-40 overflow-y-auto space-y-1 rounded-xl border border-border-subtle p-2">
                  {products.map((p) => (
                    <label
                      key={p.externalProductId}
                      className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer transition-colors ${
                        selectedProductIds.includes(p.externalProductId)
                          ? 'bg-purple-500/10 border border-purple-500/20'
                          : 'hover:bg-surface-hover/30 border border-transparent'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedProductIds.includes(p.externalProductId)}
                        onChange={() => {
                          setSelectedProductIds(prev =>
                            prev.includes(p.externalProductId)
                              ? prev.filter(id => id !== p.externalProductId)
                              : [...prev, p.externalProductId]
                          );
                        }}
                        className="w-3.5 h-3.5 rounded border-border bg-surface-hover text-purple-400 focus:ring-purple-400"
                      />
                      <span className="text-sm text-text-primary truncate flex-1">{p.productName}</span>
                      {p.sku && <span className="text-[10px] text-text-muted">{p.sku}</span>}
                    </label>
                  ))}
                </div>
              </div>

              {/* Account selector */}
              <div>
                <label className="block text-sm font-medium text-text-secondary mb-1.5">Konto reklamowe</label>
                <select
                  value={selectedAccountDbId}
                  onChange={(e) => handleAccountChange(e.target.value)}
                  className="input w-full"
                >
                  <option value="">— Wybierz konto —</option>
                  {fbAccounts.map((acc) => (
                    <option key={acc.id} value={acc.id}>
                      {acc.accountName || acc.accountId}
                    </option>
                  ))}
                </select>
              </div>

              {/* Campaign selector */}
              {selectedAccountDbId && (
                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-1.5">
                    Kampanie {selectedCampaignIds.length > 0 && <span className="text-accent-primary">({selectedCampaignIds.length})</span>}
                  </label>
                  {loadingCampaigns ? (
                    <div className="flex items-center gap-2 py-3 text-sm text-text-muted">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Ładowanie kampanii...
                    </div>
                  ) : campaigns.length === 0 ? (
                    <p className="text-sm text-text-muted py-2">Brak kampanii na tym koncie</p>
                  ) : (
                    <div className="max-h-48 overflow-y-auto space-y-1 rounded-xl border border-border-subtle p-2">
                      {campaigns.map((campaign) => (
                        <label
                          key={campaign.id}
                          className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer transition-colors ${
                            selectedCampaignIds.includes(campaign.id)
                              ? 'bg-accent-primary/5 border border-accent-primary/20'
                              : 'hover:bg-surface-hover/30 border border-transparent'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={selectedCampaignIds.includes(campaign.id)}
                            onChange={() => toggleCampaign(campaign.id)}
                            className="w-3.5 h-3.5 rounded border-border bg-surface-hover text-accent-primary focus:ring-accent-primary"
                          />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-text-primary truncate">{campaign.name}</p>
                          </div>
                          <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                            campaign.status === 'ACTIVE' ? 'text-success bg-success/10' : 'text-text-muted bg-surface-hover'
                          }`}>
                            {campaign.status === 'ACTIVE' ? 'Aktywna' : campaign.status}
                          </span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between px-6 py-4 border-t border-border-subtle">
              <button
                onClick={() => setShowDialog(false)}
                className="px-4 py-2 rounded-lg text-sm text-text-secondary hover:text-text-primary border border-border hover:border-border-strong transition-colors"
              >
                Anuluj
              </button>
              <button
                onClick={handleSaveMapping}
                disabled={savingMapping || selectedProductIds.length === 0 || !selectedAccountDbId || selectedCampaignIds.length === 0}
                className="flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-medium text-text-primary bg-accent-primary/20 border border-accent-primary/30 hover:bg-accent-primary/30 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {savingMapping && <Loader2 className="w-4 h-4 animate-spin" />}
                <Save className="w-4 h-4" />
                Zapisz
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Decimal input – keeps raw string during editing so "0." / "0,8" are preserved
// ---------------------------------------------------------------------------
function DecimalInput({
  value,
  onChange,
  placeholder,
}: {
  value: number;
  onChange: (v: number) => void;
  placeholder?: string;
}) {
  const [raw, setRaw] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const displayValue = raw !== null ? raw : value ? String(value) : '';

  return (
    <input
      ref={inputRef}
      type="text"
      inputMode="decimal"
      value={displayValue}
      placeholder={placeholder}
      className="input text-sm"
      onFocus={() => {
        setRaw(value ? String(value) : '');
      }}
      onChange={(e) => {
        const val = e.target.value;
        if (val === '' || /^\d*[.,]?\d*$/.test(val)) {
          setRaw(val);
        }
      }}
      onBlur={() => {
        if (raw !== null) {
          const num = parseFloat(raw.replace(',', '.'));
          onChange(isNaN(num) ? 0 : num);
          setRaw(null);
        }
      }}
    />
  );
}

// ---------------------------------------------------------------------------
// Store belt sub-component for nesting store content inside cost sections
// ---------------------------------------------------------------------------
function StoreBelt({
  store,
  expanded,
  onToggle,
  children,
}: {
  store: StoreInfo;
  expanded: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-lg border border-purple-500/25 overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full flex items-center gap-2.5 px-4 py-3 bg-gradient-to-r from-purple-500/15 via-purple-500/8 to-transparent hover:from-purple-500/25 transition-colors"
      >
        <span className="text-base">🏪</span>
        <span className="text-sm font-medium text-text-primary flex-1 text-left">{store.name}</span>
        {store.platform && (
          <span className="text-[10px] text-purple-300/60 uppercase tracking-wide">{store.platform}</span>
        )}
        {expanded ? (
          <ChevronDown className="w-4 h-4 text-purple-300/70" />
        ) : (
          <ChevronRight className="w-4 h-4 text-purple-300/70" />
        )}
      </button>
      {expanded && (
        <div className="p-4 border-t border-purple-500/15">
          {children}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Monthly costs summary calculated by the cashflow engine (consistent with Cashflow)
// ---------------------------------------------------------------------------

interface CashflowCostTotals {
  costProducts: number;
  costShipping: number;
  costReturns?: number;
  costFees: number;
  costFixed: number;
  adSpend: number;
}

/** Current date as YYYY-MM-DD in Warsaw timezone (not UTC) */
function warsawTodayISO(): string {
  return new Intl.DateTimeFormat('sv-SE', { timeZone: 'Europe/Warsaw' }).format(new Date());
}

function MonthlyCostsSummary({ stores }: { stores: StoreInfo[] }) {
  const [month, setMonth] = useState(() => warsawTodayISO().slice(0, 7));
  const [storeId, setStoreId] = useState('all');
  const [totals, setTotals] = useState<CashflowCostTotals | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchTotals = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [year, monthNum] = month.split('-').map(Number);
      const lastDay = new Date(year, monthNum, 0).getDate();
      const monthEnd = `${month}-${String(lastDay).padStart(2, '0')}`;
      const today = warsawTodayISO();
      const res = await financeApi.getCashflow({
        dateFrom: `${month}-01`,
        dateTo: monthEnd > today ? today : monthEnd,
        completedOnly: true,
        ...(storeId !== 'all' ? { storeIds: storeId } : {}),
      });
      const payload = (res.data ?? res) as { totals: CashflowCostTotals };
      setTotals(payload.totals);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Nie udało się pobrać podsumowania kosztów');
      setTotals(null);
    } finally {
      setLoading(false);
    }
  }, [month, storeId]);

  useEffect(() => {
    fetchTotals();
  }, [fetchTotals]);

  const rows: { label: string; value: number }[] = totals
    ? [
        { label: 'Koszty produktów', value: totals.costProducts },
        { label: 'Koszty wysyłki', value: totals.costShipping },
        { label: 'Zwroty nieodebranych paczek', value: totals.costReturns ?? 0 },
        { label: 'Prowizje płatności', value: totals.costFees },
        { label: 'Koszty stałe', value: totals.costFixed },
        { label: 'Reklama Meta', value: totals.adSpend },
      ]
    : [];
  const totalSum = rows.reduce((sum, r) => sum + r.value, 0);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-4">
        <div className="flex flex-col gap-1">
          <label className="text-xs text-text-secondary">Miesiąc</label>
          <input
            type="month"
            value={month}
            max={warsawTodayISO().slice(0, 7)}
            onChange={(e) => {
              if (e.target.value) setMonth(e.target.value);
            }}
            className="px-3 py-2 rounded-lg bg-surface-base border border-border text-text-primary text-sm focus:outline-none focus:border-accent-primary/50 transition-colors"
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs text-text-secondary">Sklep</label>
          <Select value={storeId} onChange={(e) => setStoreId(e.target.value)}>
            <option value="all">Wszystkie sklepy</option>
            {stores.map((s) => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </Select>
        </div>
      </div>

      {loading && (
        <div className="flex items-center gap-2 text-sm text-text-secondary">
          <Loader2 className="w-4 h-4 animate-spin" />
          Wczytywanie podsumowania...
        </div>
      )}

      {error && !loading && (
        <div className="flex flex-wrap items-center gap-3">
          <p className="text-sm text-danger">{error}</p>
          <button
            onClick={fetchTotals}
            className="px-3 py-1.5 rounded-lg bg-accent-primary/20 text-accent-primary text-sm border border-accent-primary/30 hover:bg-accent-primary/30 transition-colors"
          >
            Spróbuj ponownie
          </button>
        </div>
      )}

      {!loading && !error && totals && (
        <>
          <table className="w-full max-w-xl text-sm">
            <tbody>
              {rows.map((r) => (
                <tr key={r.label} className="border-b border-border-subtle">
                  <td className="py-2 text-text-secondary">{r.label}</td>
                  <td className="py-2 text-right text-text-primary">{formatPLN(r.value)}</td>
                </tr>
              ))}
              <tr className="border-t-2 border-accent-primary/30 font-bold">
                <td className="py-2 text-accent-primary uppercase tracking-wider">Razem</td>
                <td className="py-2 text-right text-accent-primary">{formatPLN(totalSum)}</td>
              </tr>
            </tbody>
          </table>
          <p className="text-xs text-text-muted">
            Kwoty netto z silnika cashflow za wybrany miesiąc. Reklama Meta w kwocie netto; szczegóły w Cashflow.
          </p>
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main Page
// ---------------------------------------------------------------------------

interface StoreInfo {
  id: string;
  name: string;
  platform?: string;
}

export function FinanceCostsPage() {
  const [searchParams] = useSearchParams();
  const section = searchParams.get('section') ?? '';
  const requestedSection = ['products', 'shipping', 'fees', 'adMappings', 'metaSource'].includes(section) ? section : null;
  const requestedStore = searchParams.get('storeId');
  const requestedProduct = searchParams.get('productId') ?? undefined;
  const [expanded, setExpanded] = useState<Record<string, boolean>>(() => requestedSection
    ? { total: false, [requestedSection]: true, ...(requestedStore ? { [`${requestedSection}_${requestedStore}`]: true } : {}) } : {});
  const [isVat, setIsVat] = useState(false);
  const [vatRate, setVatRate] = useState(23);
  const [stores, setStores] = useState<StoreInfo[]>([]);

  useEffect(() => {
    financeApi.getConfig().then((res) => {
      const cfg = (res.data ?? res) as { vatEnabled?: boolean; vatRate?: number };
      setIsVat(!!cfg.vatEnabled);
      setVatRate(cfg.vatRate ?? 23);
    }).catch(() => {});
    storeApi.getAll().then((res) => {
      const list = ((res.data ?? res) as StoreInfo[]) || [];
      setStores(list);
    }).catch(() => {});
  }, []);

  const toggle = (key: string) => {
    setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-text-primary">Koszty</h1>
        <p className="text-text-secondary mt-2">
          Zarządzaj kosztami zakupu produktów, wysyłki, prowizji transakcyjnych i kosztów stałych.
          {isVat
            ? ' Kwoty w regułach kosztów podawaj netto (bez VAT).'
            : ' Kwoty w regułach kosztów podawaj brutto.'}
          {' W dokumentach kosztów okresu wybierz podstawę kwoty zgodną z dokumentem.'}
        </p>
      </div>

      {/* Section 0: Monthly totals from the cashflow engine — expanded by default */}
      <Section
        title="Razem"
        icon={<Sigma className="w-5 h-5" />}
        expanded={expanded.total !== false}
        onToggle={() => setExpanded((prev) => ({ ...prev, total: prev.total === false }))}
      >
        <MonthlyCostsSummary stores={stores} />
      </Section>

      {/* Section 1: Product costs — with per-store belts inside */}
      <Section
        title="Koszty zakupu produktów"
        icon={<Package className="w-5 h-5" />}
        expanded={!!expanded.products}
        onToggle={() => toggle('products')}
      >
        {stores.length > 1 ? (
          <div className="space-y-3">
            {stores.map((store) => (
              <StoreBelt
                key={store.id}
                store={store}
                expanded={!!expanded[`products_${store.id}`]}
                onToggle={() => toggle(`products_${store.id}`)}
              >
                <ProductCostsSection isVat={isVat} vatRate={vatRate} fixedStoreId={store.id} initialSearch={store.id === requestedStore ? requestedProduct : undefined} />
              </StoreBelt>
            ))}
          </div>
        ) : (
          <ProductCostsSection isVat={isVat} vatRate={vatRate} fixedStoreId={stores[0]?.id} initialSearch={requestedProduct} />
        )}
        <div className="mt-4 border-t border-border-subtle pt-4">
          <button className="text-sm text-accent-primary" aria-expanded={!!expanded.unassignedProducts} onClick={() => toggle('unassignedProducts')}>Koszty bez przypisanego sklepu</button>
          {expanded.unassignedProducts && <div className="mt-3 space-y-3"><p className="text-sm text-warning">Te koszty nie są uwzględniane w wyniku sklepów. Wybierz sklep i zapisz, aby zachować koszt wraz z jego historią.</p><ProductCostsSection isVat={isVat} vatRate={vatRate} fixedStoreId="unassigned" /></div>}
        </div>
      </Section>

      {/* Section 2: Shipping costs — with per-store belts inside */}
      <Section
        title="Koszty wysyłki"
        icon={<Truck className="w-5 h-5" />}
        expanded={!!expanded.shipping}
        onToggle={() => toggle('shipping')}
      >
        {stores.length > 1 ? (
          <div className="space-y-3">
            {stores.map((store) => (
              <StoreBelt
                key={store.id}
                store={store}
                expanded={!!expanded[`shipping_${store.id}`]}
                onToggle={() => toggle(`shipping_${store.id}`)}
              >
                <ShippingCostsSection isVat={isVat} storeId={store.id} />
              </StoreBelt>
            ))}
          </div>
        ) : (
          <ShippingCostsSection isVat={isVat} storeId={stores[0]?.id} />
        )}
      </Section>

      {/* Section 3: Ad mappings — with per-store belts inside */}
      <Section
        title="Powiązanie z reklamami"
        icon={<Megaphone className="w-5 h-5" />}
        expanded={!!expanded.adMappings}
        onToggle={() => toggle('adMappings')}
      >
        {stores.length > 1 ? (
          <div className="space-y-3">
            {stores.map((store) => (
              <StoreBelt
                key={store.id}
                store={store}
                expanded={!!expanded[`adMappings_${store.id}`]}
                onToggle={() => toggle(`adMappings_${store.id}`)}
              >
                <AdMappingsSection storeId={store.id} />
              </StoreBelt>
            ))}
          </div>
        ) : (
          <AdMappingsSection storeId={stores[0]?.id} />
        )}
      </Section>

      <Section title="Źródło wydatków Meta" icon={<Megaphone className="w-5 h-5" />} expanded={!!expanded.metaSource} onToggle={()=>toggle('metaSource')}>
        <FinanceMetaCostPanel stores={stores}/>
      </Section>
      <Section title="Dokumenty kosztów okresu" icon={<Building2 className="w-5 h-5" />} expanded={!!expanded.periodCosts} onToggle={()=>toggle('periodCosts')}>
        <FinancePeriodCostPanel stores={stores}/>
      </Section>

      {/* Section 4: Transaction fees — with per-store belts inside */}
      <Section
        title="Prowizje transakcyjne"
        icon={<CreditCard className="w-5 h-5" />}
        expanded={!!expanded.fees}
        onToggle={() => toggle('fees')}
      >
        {stores.length > 1 ? (
          <div className="space-y-3">
            {stores.map((store) => (
              <StoreBelt
                key={store.id}
                store={store}
                expanded={!!expanded[`fees_${store.id}`]}
                onToggle={() => toggle(`fees_${store.id}`)}
              >
                <TransactionFeesSection storeId={store.id} />
              </StoreBelt>
            ))}
          </div>
        ) : (
          <TransactionFeesSection storeId={stores[0]?.id} />
        )}
      </Section>

      {/* Section 5: Fixed monthly costs — with per-store belts inside */}
      <Section
        title="Koszty stałe miesięczne"
        icon={<Building2 className="w-5 h-5" />}
        expanded={!!expanded.fixed}
        onToggle={() => toggle('fixed')}
      >
        {stores.length > 1 ? (
          <div className="space-y-3">
            {stores.map((store) => (
              <StoreBelt
                key={store.id}
                store={store}
                expanded={!!expanded[`fixed_${store.id}`]}
                onToggle={() => toggle(`fixed_${store.id}`)}
              >
                <FixedCostsSection isVat={isVat} storeId={store.id} />
              </StoreBelt>
            ))}
          </div>
        ) : (
          <FixedCostsSection isVat={isVat} storeId={stores[0]?.id} />
        )}
      </Section>
    </div>
  );
}
